/* GLUE — view-in-room geometry core (Stylist S-tier, Tier 1 GLUE + Tier 2 hooks)
   Pure browser JS, no dependencies. Exposes window.GLUE.
   - Homography via normalized DLT (Hartley) + exact 8x8 solve (4 correspondences)
   - Single-view metrology: rectangle quad + principal-point assumption → metric
     rectification of the wall plane up to uniform scale; a two-tap reference
     object (door ≈ 203 cm, switch plate, credit card, ceiling line) fixes scale
   - Mesh-warp Canvas2D renderer: tessellated affine approximation of the
     projective warp (the only way to draw projective transforms on 2D canvas)
   - Lighting grade + contact shadow (Tier 2 quiet-restraint versions)
   - Intake helpers: EXIF-oriented image loading, HEIC hook (heic2any when present)
*/
window.GLUE = (function () {
  'use strict';

  /* ————— 3x3 matrix helpers (row-major arrays of 9) ————— */
  function m3mul(A, B) {
    const C = new Array(9).fill(0);
    for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++)
      for (let k = 0; k < 3; k++) C[r * 3 + c] += A[r * 3 + k] * B[k * 3 + c];
    return C;
  }
  function m3apply(H, x, y) {
    const w = H[6] * x + H[7] * y + H[8];
    return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
  }
  function m3inv(H) {
    const [a, b, c, d, e, f, g, h, i] = H;
    const A = e * i - f * h, B = c * h - b * i, C = b * f - c * e;
    const det = a * A + d * B + g * C;
    if (!isFinite(det) || Math.abs(det) < 1e-12) return null;
    const D = f * g - d * i, E = a * i - c * g, F = c * d - a * f;
    const G = d * h - e * g, I = b * g - a * h, J = a * e - b * d;
    return [A / det, B / det, C / det, D / det, E / det, F / det, G / det, I / det, J / det];
  }
  function m3T(H) { return [H[0], H[3], H[6], H[1], H[4], H[7], H[2], H[5], H[8]]; }

  /* line through two points (cross product), intersection of two lines */
  const line = (p, q) => [p[1] - q[1], q[0] - p[0], p[0] * q[1] - q[0] * p[1]].map((v, i) =>
    i === 2 ? v : v); // ax+by+c=0 via cross of homogeneous points:
  function lineThrough(p, q) {
    return [
      p[1] * 1 - 1 * q[1],        // py - qy
      1 * q[0] - p[0] * 1,        // qx - px
      p[0] * q[1] - p[1] * q[0]   // px*qy - py*qx
    ];
  }
  function lineIntersect(l1, l2) {
    const x = l1[1] * l2[2] - l1[2] * l2[1];
    const y = l1[2] * l2[0] - l1[0] * l2[2];
    const w = l1[0] * l2[1] - l1[1] * l2[0];
    if (Math.abs(w) < 1e-9) return [Infinity * Math.sign(x || 1), Infinity * Math.sign(y || 1)];
    return [x / w, y / w];
  }

  /* ————— homography: src[4], dst[4] arrays of [x,y]; order TL,TR,BR,BL ————— */
  function solveH(src, dst) {
    // Hartley normalization: centroid at origin, mean distance sqrt(2)
    function norm(pts) {
      let cx = 0, cy = 0;
      pts.forEach(p => { cx += p[0]; cy += p[1]; });
      cx /= 4; cy /= 4;
      let d = 0;
      pts.forEach(p => { d += Math.hypot(p[0] - cx, p[1] - cy); });
      const s = (4 * Math.SQRT2) / (d || 1);
      return { T: [s, 0, -s * cx, 0, s, -s * cy, 0, 0, 1], pts: pts.map(p => [s * (p[0] - cx), s * (p[1] - cy)]) };
    }
    const ns = norm(src), nd = norm(dst);
    // 8x8 system: for each (x,y)->(u,v):
    //  [-x,-y,-1, 0, 0, 0, ux, uy] h = u
    //  [ 0, 0, 0,-x,-y,-1, vx, vy] h = v
    const A = [], b = [];
    for (let i = 0; i < 4; i++) {
      const [x, y] = ns.pts[i], [u, v] = nd.pts[i];
      A.push([-x, -y, -1, 0, 0, 0, u * x, u * y]); b.push(-u);
      A.push([0, 0, 0, -x, -y, -1, v * x, v * y]); b.push(-v);
    }
    const h = gauss8(A, b);
    if (!h) return null;
    const Hn = [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
    // denormalize: H = T_dst^-1 * Hn * T_src
    const Tdi = m3inv(nd.T);
    if (!Tdi) return null;
    return m3mul(Tdi, m3mul(Hn, ns.T));
  }

  /* Gaussian elimination with partial pivoting, n<=9 */
  function gauss8(A, b) {
    const n = b.length, M = A.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < n; col++) {
      let piv = col;
      for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
      if (Math.abs(M[piv][col]) < 1e-12) return null;
      [M[col], M[piv]] = [M[piv], M[col]];
      for (let r = 0; r < n; r++) {
        if (r === col) continue;
        const f = M[r][col] / M[col][col];
        for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
      }
    }
    return M.map((row, i) => row[n] / M[i][i]);
  }

  /* canonicalize wall-frame orientation: +x along the quad's top edge (TL→TR),
     +y along the quad's left edge (TL→BL, i.e. down the wall in the image) —
     so artQuad can emit corners in TL,TR,BR,BL order matching the artwork. */
  function canonWallFrame(quad, i2w, w2i) {
    const tl = quad[0], tr = quad[1], bl = quad[3];
    const c0 = m3apply(i2w, tl[0], tl[1]);
    const ex = 1e-3;
    const px = m3apply(w2i, c0[0] + ex, c0[1]);
    const py = m3apply(w2i, c0[0], c0[1] + ex);
    const fx = ((px[0] - tl[0]) * (tr[0] - tl[0]) + (px[1] - tl[1]) * (tr[1] - tl[1])) >= 0 ? 1 : -1;
    const fy = ((py[0] - tl[0]) * (bl[0] - tl[0]) + (py[1] - tl[1]) * (bl[1] - tl[1])) >= 0 ? 1 : -1;
    const F = [fx, 0, 0, 0, fy, 0, 0, 0, 1];
    return { i2w: m3mul(F, i2w), w2i: m3mul(w2i, F) };
  }

  /* ————— single-view metrology on a rectangle quad —————
     quad: [TL,TR,BR,BL] image px; imgW/imgH: photo pixels.
     focalPx: optional focal length in px (from EXIF 35mm-equivalent, or estimate).
     Cases: both vanishing points finite → f from orthogonality; one at infinity
     (level camera) → d=(0,1,0) with EXIF/assumed f; both at infinity → affine.
     Returns { i2w, w2i, focal, metric } — metric=false marks approximate mode. */
  function rectifyWall(quad, imgW, imgH, focalPx) {
    const [TL, TR, BR, BL] = quad;
    const vX = lineIntersect(lineThrough(TL, TR), lineThrough(BL, BR)); // horizontal direction
    const vY = lineIntersect(lineThrough(TL, BL), lineThrough(TR, BR)); // vertical direction
    const cx = imgW / 2, cy = imgH / 2;
    const INF = 60 * Math.max(imgW, imgH);
    const infX = !isFinite(vX[0]) || !isFinite(vX[1]) || Math.hypot(vX[0] - cx, vX[1] - cy) > INF;
    const infY = !isFinite(vY[0]) || !isFinite(vY[1]) || Math.hypot(vY[0] - cx, vY[1] - cy) > INF;
    let focal = null;
    if (!infX && !infY) {
      const f2 = -((vX[0] - cx) * (vY[0] - cx) + (vX[1] - cy) * (vY[1] - cy));
      if (f2 > (imgW * 0.05) ** 2 && f2 < (imgW * 20) ** 2) focal = Math.sqrt(f2);
    }
    if (!focal && focalPx && focalPx > imgW * 0.2 && focalPx < imgW * 20) focal = focalPx;
    if (focal && !(infX && infY)) {
      const K = [focal, 0, cx, 0, focal, cy, 0, 0, 1];
      const Ki = m3inv(K);
      let d1, d2;
      if (infY && !infX) {          // level camera: verticals stay vertical
        d2 = [0, 1, 0];
        const t = m3applyRaw(Ki, vX[0], vX[1]);
        d1 = norm3([t[0], 0, t[2]]); // strip y: horizontal direction in camera frame
        if (Math.hypot(d1[0], d1[2]) < 1e-6) d1 = [1, 0, 0];
      } else if (infX && !infY) {
        d1 = [1, 0, 0];
        const t = m3applyRaw(Ki, vY[0], vY[1]);
        d2 = norm3([0, t[1], t[2]]);
      } else {
        d1 = norm3(m3applyRaw(Ki, vX[0], vX[1]));
        d2 = norm3(m3applyRaw(Ki, vY[0], vY[1]));
      }
      let n = norm3(cross3(d1, d2));
      // wall normal must face the camera (negative z in camera frame)
      if (n[2] > 0) n = [-n[0], -n[1], -n[2]];
      const R = [d1[0], d2[0], n[0], d1[1], d2[1], n[1], d1[2], d2[2], n[2]];
      const Rt = m3T(R);
      const fr = canonWallFrame(quad, m3mul(Rt, Ki), m3mul(K, R));
      return { i2w: fr.i2w, w2i: fr.w2i, quad, focal, metric: true, approxFocal: !focalPx && (infX || infY) };
    }
    if (focal && infX && infY) {
      // fronto-parallel: pure affine rectification, scale = depth (fixed by reference)
      const fr = canonWallFrame(quad,
        [1 / focal, 0, -cx / focal, 0, 1 / focal, -cy / focal, 0, 0, 1],
        [focal, 0, cx, 0, focal, cy, 0, 0, 1]);
      return { i2w: fr.i2w, w2i: fr.w2i, quad, focal, metric: true, approxFocal: true };
    }
    // no focal information at all: quad→rectangle, aspect from mean edge lengths
    const wTop = Math.hypot(TR[0] - TL[0], TR[1] - TL[1]);
    const wBot = Math.hypot(BR[0] - BL[0], BR[1] - BL[1]);
    const hL = Math.hypot(BL[0] - TL[0], BL[1] - TL[1]);
    const hR = Math.hypot(BR[0] - TR[0], BR[1] - TR[1]);
    const W = 1000, H = Math.max(100, Math.round(W * ((hL + hR) / 2) / ((wTop + wBot) / 2 || 1)));
    const i2w = solveH(quad, [[0, 0], [W, 0], [W, H], [0, H]]);
    const w2i = i2w ? m3inv(i2w) : null;
    return { i2w, w2i, focal: null, metric: false };
  }
  function m3applyRaw(H, x, y) { return [H[0] * x + H[1] * y + H[2], H[3] * x + H[4] * y + H[5], H[6] * x + H[7] * y + H[8]]; }
  function norm3(v) { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; }
  function cross3(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
  const w2wall = (rect, x, y) => m3apply(rect.i2w, x, y);  // image px → wall units
  const wall2img = (rect, x, y) => m3apply(rect.w2i, x, y); // wall units → image px

  /* reference calibration: two image points + known cm → cm per wall unit */
  function calibrate(rect, imgPtA, imgPtB, knownCm) {
    const a = w2wall(rect, imgPtA[0], imgPtA[1]);
    const b = w2wall(rect, imgPtB[0], imgPtB[1]);
    const du = Math.hypot(a[0] - b[0], a[1] - b[1]);
    return du > 1e-9 ? knownCm / du : null; // cm per wall unit
  }

  /* artwork quad on the image. centerWallUnits: center in rectified wall units
     (from w2wall); wCm/hCm: true physical size; cmPerUnit: calibration scale. */
  function artQuad(rect, cmPerUnit, centerWallUnits, wCm, hCm) {
    const hw = (wCm / cmPerUnit) / 2, hh = (hCm / cmPerUnit) / 2;
    const [cx, cy] = centerWallUnits;
    return [
      wall2img(rect, cx - hw, cy - hh),
      wall2img(rect, cx + hw, cy - hh),
      wall2img(rect, cx + hw, cy + hh),
      wall2img(rect, cx - hw, cy + hh)
    ];
  }

  /* ————— affine from 3 point pairs (for the mesh warp) ————— */
  function affineFromTriangles(s, d) {
    // s,d: [[x,y],[x,y],[x,y]]
    const [s0, s1, s2] = s, [d0, d1, d2] = d;
    const den = s0[0] * (s1[1] - s2[1]) + s1[0] * (s2[1] - s0[1]) + s2[0] * (s0[1] - s1[1]);
    if (Math.abs(den) < 1e-9) return null;
    const a = (d0[0] * (s1[1] - s2[1]) + d1[0] * (s2[1] - s0[1]) + d2[0] * (s0[1] - s1[1])) / den;
    const c = (d0[0] * (s2[0] - s1[0]) + d1[0] * (s0[0] - s2[0]) + d2[0] * (s1[0] - s0[0])) / den;
    const e = (d0[0] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[0] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[0] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
    const b = (d0[1] * (s1[1] - s2[1]) + d1[1] * (s2[1] - s0[1]) + d2[1] * (s0[1] - s1[1])) / den;
    const dd = (d0[1] * (s2[0] - s1[0]) + d1[1] * (s0[0] - s2[0]) + d2[1] * (s1[0] - s0[0])) / den;
    const f = (d0[1] * (s1[0] * s2[1] - s2[0] * s1[1]) + d1[1] * (s2[0] * s0[1] - s0[0] * s2[1]) + d2[1] * (s0[0] * s1[1] - s1[0] * s0[1])) / den;
    return [a, b, c, dd, e, f];
  }

  /* ————— the mesh-warp renderer —————
     Draw `src` (canvas/image) projectively onto dstQuad ([TL,TR,BR,BL]) in ctx.
     N = mesh resolution. fill: if set, draw a flat color instead of the image
     (used for the contact-shadow silhouette). expand: px seam-hiding overlap. */
  function warpDraw(ctx, src, dstQuad, opts) {
    opts = opts || {};
    const N = opts.mesh || 20, expand = opts.expand == null ? 0.6 : opts.expand;
    const SW = src.width || src.naturalWidth, SH = src.height || src.naturalHeight;
    if (!SW || !SH) return;
    const H = solveH([[0, 0], [SW, 0], [SW, SH], [0, SH]], dstQuad);
    if (!H) return;
    ctx.save();
    if (opts.alpha != null) ctx.globalAlpha = opts.alpha;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const x0 = (i / N) * SW, x1 = ((i + 1) / N) * SW;
      const y0 = (j / N) * SH, y1 = ((j + 1) / N) * SH;
      const p00 = m3apply(H, x0, y0), p10 = m3apply(H, x1, y0);
      const p11 = m3apply(H, x1, y1), p01 = m3apply(H, x0, y1);
      drawTri(ctx, src, [[x0, y0], [x1, y0], [x0, y1]], [p00, p10, p01], expand, opts.fill);
      drawTri(ctx, src, [[x1, y0], [x1, y1], [x0, y1]], [p10, p11, p01], expand, opts.fill);
    }
    ctx.restore();
  }
  function drawTri(ctx, src, sTri, dTri, expand, fill) {
    const t = affineFromTriangles(sTri, dTri);
    if (!t) return;
    // expand the destination triangle slightly to hide seams
    const cx = (dTri[0][0] + dTri[1][0] + dTri[2][0]) / 3;
    const cy = (dTri[0][1] + dTri[1][1] + dTri[2][1]) / 3;
    ctx.save();
    ctx.beginPath();
    dTri.forEach((p, i) => {
      const dx = p[0] - cx, dy = p[1] - cy;
      const l = Math.hypot(dx, dy) || 1;
      const ex = p[0] + (dx / l) * expand, ey = p[1] + (dy / l) * expand;
      i ? ctx.lineTo(ex, ey) : ctx.moveTo(ex, ey);
    });
    ctx.closePath();
    ctx.clip();
    ctx.setTransform(t[0], t[1], t[2], t[3], t[4], t[5]);
    if (fill) {
      const SW = src.width || src.naturalWidth, SH = src.height || src.naturalHeight;
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, SW, SH);
    } else {
      ctx.drawImage(src, 0, 0);
    }
    ctx.restore();
  }

  /* ————— Tier 2: wall lighting read (quiet version) —————
     Samples the photo inside the wall quad (rectified grid), returns mean
     luminance, warmth (r-b), and a coarse light-direction vector in image px. */
  function readWallLighting(photo, quad) {
    const S = 48;
    const cv = document.createElement('canvas');
    cv.width = photo.width || photo.naturalWidth; cv.height = photo.height || photo.naturalHeight;
    const g = cv.getContext('2d', { willReadFrequently: true });
    g.drawImage(photo, 0, 0);
    const H = solveH([[0, 0], [S, 0], [S, S], [0, S]], quad);
    if (!H) return null;
    let lum = 0, warm = 0, n = 0;
    const colL = [], colR = [], rowT = [], rowB = [];
    for (let j = 0; j < S; j++) for (let i = 0; i < S; i++) {
      const [x, y] = m3apply(H, i + .5, j + .5);
      const xi = Math.round(x), yi = Math.round(y);
      if (xi < 0 || yi < 0 || xi >= cv.width || yi >= cv.height) continue;
      const d = g.getImageData(xi, yi, 1, 1).data;
      const L = .2126 * d[0] + .7152 * d[1] + .0722 * d[2];
      lum += L; warm += (d[0] - d[2]); n++;
      (i < S / 2 ? colL : colR).push(L);
      (j < S / 2 ? rowT : rowB).push(L);
    }
    if (!n) return null;
    const mean = a => a.reduce((x, y) => x + y, 0) / (a.length || 1);
    const gx = mean(colR) - mean(colL), gy = mean(rowB) - mean(rowT);
    return {
      lum: lum / n, warm: warm / n,
      lightDir: [-(gx || 0), -(gy || 0)], // direction light comes FROM (toward darker side)
      gradMag: Math.hypot(gx, gy)
    };
  }

  /* grade an artwork canvas toward the wall's light: Δ luminance, Δ warmth */
  function gradeCanvas(canvas, dLum, dWarm) {
    if (!dLum && !dWarm) return;
    const g = canvas.getContext('2d', { willReadFrequently: true });
    const im = g.getImageData(0, 0, canvas.width, canvas.height);
    const d = im.data;
    const l = Math.max(-40, Math.min(40, dLum)) * 0.6;   // quiet restraint: cap the grade
    const w = Math.max(-30, Math.min(30, dWarm)) * 0.5;
    for (let i = 0; i < d.length; i += 4) {
      d[i]     = clamp8(d[i] + l + w);
      d[i + 1] = clamp8(d[i + 1] + l);
      d[i + 2] = clamp8(d[i + 2] + l - w);
    }
    g.putImageData(im, 0, 0);
  }
  const clamp8 = v => v < 0 ? 0 : v > 255 ? 255 : v;

  /* ————— intake: EXIF-oriented load + HEIC hook ————— */
  async function loadImageFile(file) {
    let f = file;
    const isHeic = /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');
    if (isHeic) {
      if (window.heic2any) {
        const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
        f = Array.isArray(out) ? out[0] : out;
      } else {
        throw new Error('heic');
      }
    }
    // EXIF orientation: createImageBitmap honors it where supported
    try {
      const bmp = await createImageBitmap(f, { imageOrientation: 'from-image' });
      const cv = document.createElement('canvas');
      cv.width = bmp.width; cv.height = bmp.height;
      cv.getContext('2d').drawImage(bmp, 0, 0);
      return cv;
    } catch (e) {
      const url = URL.createObjectURL(f);
      const im = new Image();
      await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
      const cv = document.createElement('canvas');
      cv.width = im.naturalWidth; cv.height = im.naturalHeight;
      cv.getContext('2d').drawImage(im, 0, 0);
      URL.revokeObjectURL(url);
      return cv;
    }
  }

  /* edge-snap: nearest strong gradient within r px of (x,y) on a precomputed
     downscaled gradient map — helps corner taps land on real wall edges */
  function buildGradientMap(photo, maxW) {
    const w = Math.min(maxW || 480, photo.width || photo.naturalWidth);
    const ar = (photo.height || photo.naturalHeight) / (photo.width || photo.naturalWidth);
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = Math.round(w * ar);
    const g = cv.getContext('2d', { willReadFrequently: true });
    g.drawImage(photo, 0, 0, cv.width, cv.height);
    const d = g.getImageData(0, 0, cv.width, cv.height).data;
    const W = cv.width, Hh = cv.height;
    const mag = new Float32Array(W * Hh);
    for (let y = 1; y < Hh - 1; y++) for (let x = 1; x < W - 1; x++) {
      const i = (y * W + x) * 4;
      const lum = o => .2126 * d[o] + .7152 * d[o + 1] + .0722 * d[o + 2];
      const gx = lum(i + 4) - lum(i - 4), gy = lum(i + W * 4) - lum(i - W * 4);
      mag[y * W + x] = Math.hypot(gx, gy);
    }
    return { mag, W, H: Hh, scale: w / (photo.width || photo.naturalWidth) };
  }
  function snapToEdge(grad, x, y, radiusPx) {
    const gx = Math.round(x * grad.scale), gy = Math.round(y * grad.scale);
    const r = Math.max(2, Math.round(radiusPx * grad.scale));
    let best = null, bestV = 30; // minimum gradient strength to snap
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const X = gx + dx, Y = gy + dy;
      if (X < 1 || Y < 1 || X >= grad.W - 1 || Y >= grad.H - 1) continue;
      const v = grad.mag[Y * grad.W + X];
      if (v > bestV) { bestV = v; best = [X / grad.scale, Y / grad.scale]; }
    }
    return best || [x, y];
  }

  /* ————— Tier 2: automatic wall-quad proposal (classical voter) —————
     Hough-lite over the gradient map: two strongest near-horizontal lines
     (ceiling/floor edges) × two strongest near-vertical lines (wall sides).
     Returns {quad:[TL,TR,BR,BL] in photo px, confidence} or null. */
  function proposeQuad(grad, photoW, photoH) {
    const W = grad.W, H = grad.H, mag = grad.mag;
    const sorted = Float32Array.from(mag).sort();
    // flat photos make percentile thresholds collapse — keep an absolute floor
    const thr = Math.max(sorted[Math.floor(sorted.length * 0.97)] || 0, 24);
    const hRho = new Float32Array(H * 2), vRho = new Float32Array(W * 2);
    for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      if (mag[i] < thr) continue;
      const gx = mag[i + 1] - mag[i - 1], gy = mag[i + W] - mag[i - W];
      const ang = Math.atan2(gy, gx) * 180 / Math.PI;
      const edgeAng = ((ang + 90) % 180 + 180) % 180;   // 0 = horizontal line
      if (edgeAng < 25 || edgeAng > 155) hRho[y + H] += mag[i];
      else if (edgeAng > 65 && edgeAng < 115) vRho[x + W] += mag[i];
    }
    // merge thick edges: 5-bin box blur so one edge = one peak
    const smooth = acc => {
      const out = new Float32Array(acc.length);
      for (let i = 0; i < acc.length; i++) {
        let s = 0, n = 0;
        for (let j = -2; j <= 2; j++) { const k = i + j; if (k >= 0 && k < acc.length) { s += acc[k]; n++; } }
        out[i] = s / n;
      }
      return out;
    };
    const hS = smooth(hRho), vS = smooth(vRho);
    function candidates(acc, off, hi) {
      // local maxima separated by ≥12 bins, votes within a floor of the strongest
      const peaks = [];
      let max = 0;
      for (let i = 0; i < hi; i++) max = Math.max(max, acc[i]);
      for (let i = 1; i < hi - 1; i++) {
        if (acc[i] < max * 0.25) continue;
        if (acc[i] >= acc[i - 1] && acc[i] >= acc[i + 1] && !peaks.some(p => Math.abs(p.i - i) < 12))
          peaks.push({ i: i - off, v: acc[i] });
      }
      return peaks.sort((a, b) => b.v - a.v);
    }
    function widestPair(cands, minSpan) {
      // the widest pair whose weaker member still carries real votes
      let best = null;
      for (let a = 0; a < cands.length; a++) for (let b = a + 1; b < cands.length; b++) {
        const span = Math.abs(cands[a].i - cands[b].i);
        if (span < minSpan) continue;
        const strength = Math.min(cands[a].v, cands[b].v);
        const score = span * 0.6 + strength * 0.4 / (cands[0].v || 1) * minSpan;
        if (!best || score > best.score) best = { a: cands[a], b: cands[b], score };
      }
      return best;
    }
    const hc = candidates(hRho, H, H * 2), vc = candidates(vRho, W, W * 2);
    const hp = widestPair(hc, H * 0.25), vp = widestPair(vc, W * 0.25);
    if (!hp || !vp) return null;
    const yA = Math.min(hp.a.i, hp.b.i), yB = Math.max(hp.a.i, hp.b.i);
    const xA = Math.min(vp.a.i, vp.b.i), xB = Math.max(vp.a.i, vp.b.i);
    const meanVotes = (hp.a.v + hp.b.v + vp.a.v + vp.b.v) / 4;
    const confidence = Math.min(0.95, meanVotes / (W * H * thr * 0.02));
    const k = 1 / grad.scale;
    return {
      quad: [[xA * k, yA * k], [xB * k, yA * k], [xB * k, yB * k], [xA * k, yB * k]],
      confidence
    };
  }

  return {
    solveH, m3apply, m3inv, m3mul, lineThrough, lineIntersect,
    rectifyWall, calibrate, artQuad, w2wall, wall2img,
    warpDraw, affineFromTriangles,
    readWallLighting, gradeCanvas,
    loadImageFile, buildGradientMap, snapToEdge, proposeQuad
  };
})();
